import React, { useCallback, useMemo } from 'react'
import slice from 'lodash/slice'
import take from 'lodash/take'
import takeRight from 'lodash/takeRight'
import { useProjectMeta } from '@/contexts/ProjectMetaContext'
import { Offer, Perk } from '@/services/PifService'
import { getPriceForCurrency } from '@/services/PriceFormatter'
import { ReactFCC } from '@/types/react'
import { sanitizeCreditNameInput } from '@/utils/credit-name-utils'
import { ExpressPerk } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifExpressOffers/TheatricalExpressAllPerkViewer'
import { useSelectOffer } from '@/views/PayItForward/UseSelectOffer'

const hasEarlyAccess = (offerPrice: number, earlyAccessMinimumPrice?: number) => {
  if (typeof earlyAccessMinimumPrice !== 'number') return false

  return offerPrice >= earlyAccessMinimumPrice
}
interface PayItForwardOffersDataContextState {
  offersData: Offer[] | []
  allPerks: { id: string; perk: Perk }[] | []
  featuredOffer: Offer | null
  selectedOffer: Offer | null
  handleSetSelectedOffer: (offer: Offer) => void
  handleSetSelectedOfferId: (offerId: string) => void
  dividedOffers: { individualOffers: Offer[] | []; theaterOffers: Offer[] | []; groupOffers: Offer[] | [] }
  handlePerkClick: (perk: ExpressPerk) => void
  nameInCredits: string
  setNameInCredits: (value: string) => void
}

interface PayItForwardOffersDataProviderProps {
  offers: Offer[]
  defaultOfferId?: string
}

const defaultValue = {
  offersData: [],
  allPerks: [],
  dividedOffers: { individualOffers: [], theaterOffers: [], groupOffers: [] },
  featuredOffer: null,
  selectedOffer: null,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handleSetSelectedOffer: (offer: Offer) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handleSetSelectedOfferId: (offerId: string) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handlePerkClick: (perk: ExpressPerk) => {},
  nameInCredits: '',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setNameInCredits: (value: string) => {},
}
export const PayItForwardOffersDataContext = React.createContext<PayItForwardOffersDataContextState>(defaultValue)

export const PayItForwardOfferDataProvider: ReactFCC<PayItForwardOffersDataProviderProps> = ({
  children,
  offers,
  defaultOfferId,
}) => {
  const { pifFeaturedOfferId, pifPerkEarlyAccessMinimumPrice } = useProjectMeta()

  const featuredOffer = useMemo(() => {
    const featureOffer = offers?.find((offer) => {
      if (offer?.id) return offer.id === pifFeaturedOfferId
    })
    if (featureOffer) return featureOffer
    return offers?.[2] ?? null
  }, [offers, pifFeaturedOfferId])

  const { selectedOffer, setSelectedOffer } = useSelectOffer({
    offers: offers,
    defaultOfferId: defaultOfferId || pifFeaturedOfferId || offers?.[4]?.id || offers?.[2]?.id || offers?.[1]?.id,
  })

  const [nameInCredits, setNameInCreditsRaw] = React.useState('')

  const setNameInCredits = useCallback((value: string) => {
    setNameInCreditsRaw(sanitizeCreditNameInput(value))
  }, [])

  const handleSetSelectedOffer = useCallback(
    (offer: Offer) => {
      setSelectedOffer(offer)
    },
    [setSelectedOffer],
  )

  const handleSetSelectedOfferId = useCallback(
    (offerId: string) => {
      const offer = offers.find((offer) => offer?.id === offerId)
      if (offer) setSelectedOffer(offer)
    },
    [offers, setSelectedOffer],
  )

  const allPerks = useMemo(() => {
    if (!offers?.length) {
      return []
    }

    const perkIds: Record<string, boolean> = {}
    const perksList = []

    for (const offer of offers) {
      const sortedProductSet = [...(offer?.offeredproductSet ?? [])].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))

      for (const perk of sortedProductSet) {
        const perkProduct = perk?.product

        if (perkProduct && !perkIds[perkProduct.id]) {
          perkIds[perkProduct.id] = true
          perksList.push({
            id: perkProduct.id,
            perk,
          })
        }
      }
    }

    return perksList
  }, [offers])

  const individualOffers = useMemo(() => take(offers, 10), [offers])
  const theaterOffers = useMemo(() => slice(offers, 9, 14), [offers])
  const groupOffers = useMemo(() => takeRight(offers, 9), [offers])

  const handlePerkClick = useCallback(
    (perk: ExpressPerk) => {
      if (perk.isSelected) return
      if (perk.isEarlyAccess) {
        const earlyAccessOffer = offers.find((o) => {
          return hasEarlyAccess(getPriceForCurrency(o.price, o.currency), pifPerkEarlyAccessMinimumPrice)
        })
        if (earlyAccessOffer) handleSetSelectedOffer(earlyAccessOffer)
        return
      }
      const offerToSelect = offers.find((o) => {
        return o.offeredproductSet.some((p) => p.product.id === perk.id)
      })
      if (offerToSelect) handleSetSelectedOffer(offerToSelect)
    },
    [pifPerkEarlyAccessMinimumPrice, handleSetSelectedOffer, offers],
  )

  const value = useMemo(() => {
    return {
      offersData: offers,
      dividedOffers: { individualOffers, theaterOffers, groupOffers },
      allPerks,
      featuredOffer,
      selectedOffer,
      handleSetSelectedOffer,
      handleSetSelectedOfferId,
      handlePerkClick,
      nameInCredits,
      setNameInCredits,
    }
  }, [
    offers,
    individualOffers,
    theaterOffers,
    groupOffers,
    allPerks,
    featuredOffer,
    selectedOffer,
    handleSetSelectedOffer,
    handleSetSelectedOfferId,
    handlePerkClick,
    nameInCredits,
    setNameInCredits,
  ])

  return <PayItForwardOffersDataContext.Provider value={value}>{children}</PayItForwardOffersDataContext.Provider>
}

export const usePayItForwardOfferData = () => React.useContext(PayItForwardOffersDataContext)
